import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ButtonWithLoader, Col, Row, success, Wizard } from '@valid-eval/shared-react-components';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import GenericModal from 'components/GenericModal';
import DNDTable from 'components/Tables/DNDTable';
import {
  useFetchDocuments,
  useReorderDocuments,
  useUpdateDocument,
} from 'data/features/documentsHooks';
import { Document } from 'data/features/documentsTypes';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';

import { EventStepComponentProps } from '.';
import { AddDocument } from './components/AddDocument';
import DocumentForm, { DocumentFormValues } from './components/DocumentForm';
import { columns } from './components/DocumentsTableDefinition';
import EditEventStepForm from './components/EditEventStepForm';

function DocumentsStepComponent({ label, subLabel, event, nextStep }: EventStepComponentProps) {
  const { t } = useTranslation();
  const { documents } = useFetchDocuments(event.id);
  const { reorderDocuments, saving } = useReorderDocuments();
  const { updateDocumentInEvent } = useUpdateDocument();
  const showErrorIfRejected = useShowErrorIfRejected();
  const [editModalOpen, openEditModal, closeEditModal] = useBooleanFlag(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | undefined>(undefined);

  const getRecipientValues = (recipient?: string) => {
    if (recipient === 'judges') return ['judges', false];
    if (recipient === 'teams') return [false, 'teams'];
    return ['judges', 'teams'];
  };

  const editForm = useForm<DocumentFormValues>({
    mode: 'all',
    values: selectedDocument
      ? {
          ...selectedDocument,
          recipient_values: getRecipientValues(selectedDocument.recipient),
          file_url: selectedDocument.url,
          file_name: selectedDocument.name,
        }
      : undefined,
  });

  const handleEdit = (document: DocumentFormValues) => {
    setSelectedDocument(document);
    openEditModal();
  };

  const handleEditSubmit = async (document: DocumentFormValues) => {
    if (!selectedDocument) return;

    const recipients = (document.recipient_values?.filter(Boolean) as ('teams' | 'judges')[]) || [];
    document.recipient = recipients.length === 1 ? recipients[0] : 'teams_and_judges';

    document.recipient_values = undefined;

    const [, hasError] = showErrorIfRejected(
      await updateDocumentInEvent(event.id, selectedDocument.id, document),
    );
    if (!hasError) {
      success(t('event.form.edit_document_success'));
      closeEditModal();
    }
  };

  const tableModel = useReactTable<any>({
    data: documents,
    meta: {
      edit: handleEdit,
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleReorderRow = async (fromIndex: number, toIndex: number) => {
    const [, hasError] = showErrorIfRejected(await reorderDocuments(event.id, fromIndex, toIndex));
    if (!hasError) success(t('event.form.reorder_document_success'));
  };

  return (
    <EditEventStepForm label={label} subLabel={subLabel}>
      <GenericModal
        name="edit-document"
        show={editModalOpen}
        onCancel={closeEditModal}
        title={t('event.form.edit_document_title')}
        body={<DocumentForm methods={editForm} event={event} onSubmit={handleEditSubmit} />}
        onConfirm={editForm.handleSubmit(handleEditSubmit)}
        confirmButton={t('common.save')}
        cancelButton={t('common.cancel')}
        isButtonDisabled={saving}
        isCancelButtonDisabled={saving}
      />
      <Row className="mt-3 mb-2">
        <Col xs={12} className="d-flex justify-content-end">
          <AddDocument event={event} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <DNDTable tableModel={tableModel} onReorderRow={handleReorderRow} />
        </Col>
      </Row>
      <Wizard.Footer>
        <div className="d-flex justify-content-end align-items-center">
          <ButtonWithLoader variant="primary" loading={saving} onClick={nextStep}>
            {t('event.form.next')}
          </ButtonWithLoader>
        </div>
      </Wizard.Footer>
    </EditEventStepForm>
  );
}

export default DocumentsStepComponent;
